import {ChangeDetectionStrategy, Component, DestroyRef, Input} from '@angular/core';
import {NgClass, PercentPipe} from "@angular/common";
import {MatDialog} from "@angular/material/dialog";
import {AksjeeierEnkelModell, AksjeeierMedSlettSisteBeholdningInfoModell, SlettSisteBeholdningsendringModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormaterProsentPipe} from "felles-ng2/pipes/FormaterProsentPipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {AksjenummerintervallerPipe} from "felles-ng2/pipes/AksjenummerintervallerPipe";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {LukkAbonnementer} from "felles-ng2/infrastruktur/rxjs/LukkAbonnementer";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AntallAksjerPerAksjeklasseKomponent} from "web-ng2/moduler/aksjeeierbok/komponenter/antall-aksjer-per-aksjeklasse/antall-aksjer-per-aksjeklasse";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {BeholdningerSkjemamodell, BeholdningSkjemamodell} from "../aksjeeiere-rediger-beholdning/aksjeeiere-rediger-beholdning";
import {Temporal} from "temporal-polyfill";
import {MatTooltipModule} from "@angular/material/tooltip";
import {Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {TranslateModule} from "@ngx-translate/core";
import {lesPlainDateSerialisert, lesPlainDateSerialisertOpt} from "felles-ng2/støttefunksjoner/temporal";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('AksjeeiereSlettSisteBeholdningsendringKomponent');


@Component({
    selector: 'aksjeeiere-slett-siste-beholdningsendring',
    templateUrl: './aksjeeiere-slett-siste-beholdningsendring.html',
    styleUrls: ['./aksjeeiere-slett-siste-beholdningsendring.scss'],
    imports: [
        FormaterPlainDatePipe,
        FormaterProsentPipe,
        PercentPipe,
        AksjenummerintervallerPipe,
        InlineMeldingKomponent,
        NgClass,
        AntallAksjerPerAksjeklasseKomponent,
        MatTooltipModule,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AksjeeiereSlettSisteBeholdningsendringKomponent {

    modell!: SlettSisteBeholdningerSkjemamodell;

    private ab = new LukkAbonnementer();

    constructor(public dialog: MatDialog, private destroyRef: DestroyRef, private oversettelser:Oversettelser) {
        this.ab.registrerHosDestroyRef(this.destroyRef);
    }

    @Input({required: true}) set slettInfoModell(slettInfoModell: SlettSisteBeholdningsendringModell) {
        this.modell = new SlettSisteBeholdningerSkjemamodell(slettInfoModell, this.ab, this.oversettelser);
    }

    //denne er her kun for å få korrekt type i templaten.. ellers må BeholdningerSkjemamodell generifiseres
    //dvs `@for (beholdningmodell of modell.beholdninger)` fører til typefeil
    beholdninger(): SlettSisteBeholdningSkjemamodell[] {
        return this.modell.beholdninger as SlettSisteBeholdningSkjemamodell[]
    }
}

//streng tatt burde BeholdningerSkjemamodell vært generifisert for å kunne angi at aksjeeiere er av typen SlettSisteBeholdningSkjemamodell[]
class SlettSisteBeholdningerSkjemamodell extends BeholdningerSkjemamodell {

    constructor(slettSisteBeholdningsendring: SlettSisteBeholdningsendringModell, ab: LukkAbonnementer, oversettelser:Oversettelser) {
        super(
          slettSisteBeholdningsendring.aksjeeiere.map(eier => eier.aksjeeier),
          slettSisteBeholdningsendring.aksjeklasser,
          ab,
          oversettelser);

        //this.slettSisteBeholdningsendring er ikke satt når nyBeholdningModell() kalles (super-kallene skjer før slettSisteBeholdningsendring har blitt tilordnet)
        //må derfor sette felter på SlettSisteBeholdningSkjemamodell instansene her
        slettSisteBeholdningsendring.aksjeeiere.forEach(eier => {
            this.beholdninger
              .filter(kandidat => kandidat.aksjeeier.id === eier.aksjeeier.id)
              .forEach(skjemamodell =>
                //vi vet at vi her operer på SlettSisteBeholdningSkjemamodell (har ikke lagt til støtte for generics arg på BeholdningSkjemamodell)
                (<SlettSisteBeholdningSkjemamodell>skjemamodell).oppdaterFraAksjeeierMedSlettSisteBeholdningInfoModell(eier))
        })
    }

    override nyBeholdningModell(aksjeeier: AksjeeierEnkelModell, forelder: BeholdningerSkjemamodell): SlettSisteBeholdningSkjemamodell {
        return new SlettSisteBeholdningSkjemamodell(aksjeeier, forelder);
    }
}

class SlettSisteBeholdningSkjemamodell extends BeholdningSkjemamodell {

    nyInnfoeringsdato: PlainDate | null = null;

    constructor(aksjeeier: AksjeeierEnkelModell, forelder: BeholdningerSkjemamodell) {
        super(aksjeeier, forelder);
    }

    oppdaterFraAksjeeierMedSlettSisteBeholdningInfoModell(infoOmBeholdningSomSlettes: AksjeeierMedSlettSisteBeholdningInfoModell) {
        this.nyInnfoeringsdato = lesPlainDateSerialisertOpt(infoOmBeholdningSomSlettes.aksjeeier.naavaerendeBeholdning?.gyldigFraTil?.start);

        if (erNil(infoOmBeholdningSomSlettes.beholdningSomSlettes)) return; //denne aksjeeierens beholdning endres ikke som følge av 'slette siste beholdningsoppdatering'

        this.nyInnfoeringsdato = null;
        this.aksjeklasseKtrl.setValue(null)
        this.aksjenummerKtrl.setValue('')

        if (erIkkeNil(infoOmBeholdningSomSlettes.nyNaavaerendeBeholdning)) {
            this.nyInnfoeringsdato = lesPlainDateSerialisert(infoOmBeholdningSomSlettes.nyNaavaerendeBeholdning.gyldigFraTil.start);
            this.aksjeklasseKtrl.setValue(infoOmBeholdningSomSlettes.nyNaavaerendeBeholdning.aksjeklasse);
            this.aksjenummerKtrl.setValue(infoOmBeholdningSomSlettes.nyNaavaerendeBeholdning.aksjenummer);
        }
    }

    harAdvarsler() {
        return this.aksjenummerKtrl.invalid;
    }

    advarslerTooltipTekst() {
        return Object.values(this.aksjenummerKtrl.errors!).join(', ')
    }
}
